// Enhanced StepOne Component
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getCategories, searchCryptos } from "../../services/WizardServices";
import { Search } from "lucide-react";
import _ from "lodash";
import "./StepOne.css";
import PropTypes from "prop-types";
import CryptoIconWrapper from "../cryptoicons/CryptoIconWrapper";

const MAX_TOKENS = 5;

const StepOne = ({
  onNext,
  onBack,
  currentStep,
  isLastStep,
  initialSelectedTokens = [],
}) => {
  const [selectedTokens, setSelectedTokens] = useState(initialSelectedTokens);
  const [categories, setCategories] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const filteredTokens = useMemo(() => {
    if (!tokens.length) return [];

    let filtered = [...tokens];

    if (activeCategory !== "all") {
      filtered = filtered.filter((token) =>
        token.categories.includes(activeCategory)
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (token) =>
          token.name.toLowerCase().includes(term) ||
          token.symbol.toLowerCase().includes(term)
      );
    }

    return _.orderBy(filtered, ["marketCap"], ["desc"]).slice(0, page * 10);
  }, [tokens, activeCategory, searchTerm, page]);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  // When Continue is clicked, pass the selected tokens upward
  const handleNext = () => {
    console.log("Step 1 data saved:", selectedTokens);
    onNext({ selectedTokens }); // Pass the selected tokens to parent
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe || isRightSwipe) {
      const currentIndex = categories.findIndex((c) => c.id === activeCategory);
      if (isLeftSwipe && currentIndex < categories.length - 1) {
        handleCategoryChange(categories[currentIndex + 1].id);
      } else if (isRightSwipe && currentIndex > 0) {
        handleCategoryChange(categories[currentIndex - 1].id);
      }
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [categoriesData, tokensData] = await Promise.all([
          getCategories(),
          searchCryptos(searchTerm, activeCategory),
        ]);
        if (mounted) {
          setCategories(categoriesData);
          setTokens(tokensData);
          setLoading(false);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        if (mounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [activeCategory]);

  const debouncedSearch = useCallback(
    _.debounce(async (term) => {
      setLoading(true);
      try {
        const data = await searchCryptos(term, activeCategory);
        setTokens(data);
        setPage(1);
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setLoading(false);
      }
    }, 300),
    [activeCategory]
  );

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setPage(1);
  };

  const handleTokenSelect = useCallback((token) => {
    setSelectedTokens((prev) => {
      const isSelected = prev.some((t) => t.id === token.id);
      if (isSelected) return prev.filter((t) => t.id !== token.id);
      if (prev.length >= MAX_TOKENS) return prev;
      return [...prev, token];
    });
  }, []);

  const handleLoadMore = () => setPage((prev) => prev + 1);
  const hasMoreTokens = filteredTokens.length >= page * 10;

  const formatMarketCap = (value) => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toLocaleString()}`;
  };

  return (
    <div className="token-wizard-container">
      <div className="wizard-header">
        <div className="wizard-header-content">
          <div className="wizard-title-container">
            <div className="wizard-step-indicator">1</div>
            <div>
              <h2 className="wizard-title">Select Tokens</h2>
              <p className="wizard-description">
                Pick tokens to include in the race
              </p>
            </div>
          </div>

          <div
            className="category-filters"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`category-button ${activeCategory === category.id ? "active" : ""
                  } ${category.id === "all" ? "all-crypto" : ""}`}
              >
                {category.label}
                {category.count > 0 && (
                  <span className="category-count">({category.count})</span>
                )}
              </button>
            ))}
          </div>

          <div className="search-container">
            <input
              type="text"
              placeholder="Choose your token"
              className="search-input"
              value={searchTerm}
              onChange={handleSearchInput}
              aria-label="Search tokens"
            />
            <Search className="search-icon" size={20} />
          </div>
        </div>
      </div>

      <div className="tokens-table-container">
        <table className="tokens-table">
          <thead>
            <tr>
              <th className="checkbox-column">Add</th>
              <th className="rank-column">#</th>
              <th>Name</th>
              <th className="price-column">Price</th>
              <th className="change-column">1h %</th>
              <th className="change-column">24h %</th>
              <th className="market-cap-column">Market Cap</th>
            </tr>
          </thead>
          <tbody>
            {filteredTokens.map((token, index) => {
              const isSelected = selectedTokens.some((t) => t.id === token.id);
              return (
                <tr
                  key={token.id}
                  onClick={() => handleTokenSelect(token)}
                  className={`token-row ${isSelected ? "selected" : ""}`}
                >
                  <td className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTokenSelect(token)}
                      className="token-checkbox"
                    />
                  </td>
                  <td className="rank-column">{index + 1}</td>
                  <td className="token-name">
                    <CryptoIconWrapper name={token.symbol.toLowerCase()} size={24} />
                    <span className="token-name-text">{token.name}</span>
                    <span className="token-symbol">{token.symbol}</span>
                  </td>
                  <td className="price-column">
                    ${token.price.toLocaleString()}
                  </td>
                  <td
                    className={`change-column ${token.change1h >= 0
                      ? "positive-change"
                      : "negative-change"
                      }`}
                  >
                    {token.change1h > 0 ? "+" : ""}
                    {token.change1h}%
                  </td>
                  <td
                    className={`change-column ${token.change24h >= 0
                      ? "positive-change"
                      : "negative-change"
                      }`}
                  >
                    {token.change24h > 0 ? "+" : ""}
                    {token.change24h}%
                  </td>
                  <td className="market-cap-column">
                    {formatMarketCap(token.marketCap)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading && (
          <div className="loading-indicator">
            <div className="loading-spinner"></div>
            Loading...
          </div>
        )}
        {!loading && hasMoreTokens && (
          <button
            className="load-more"
            onClick={handleLoadMore}
            aria-label="Load more tokens"
          >
            Load more
          </button>
        )}
      </div>


      <div className="selected-tokens-container">
        {selectedTokens.length > 0 && (
          <>
            <div className="selected-tokens-title">Selected Tokens</div>
            <div className="selected-tokens-list">
              {selectedTokens.map((token, index) => (
                <React.Fragment key={token.id}>
                  <div
                    className="token-item"
                    onClick={() => handleTokenSelect(token)}
                    title={`Remove ${token.name}`}
                  >
                    <CryptoIconWrapper name={token.symbol.toLowerCase()} size={48} />
                    {/* <span className="token-name-small">{token.symbol}</span> */}
                  </div>
                  {index < selectedTokens.length - 1 && (
                    <div className="vs-separator">VS</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="button-container">
        {currentStep > 1 && (
          <button onClick={onBack} className="back-button">
            Back
          </button>
        )}
        <button
          onClick={handleNext}
          className="continue-button"
          disabled={selectedTokens.length === 0 || isLastStep}
        >
          {isLastStep ? "Finish" : "Continue"}
        </button>
      </div>
    </div>
  );
};

StepOne.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  isLastStep: PropTypes.bool.isRequired,
  initialSelectedTokens: PropTypes.arrayOf(PropTypes.object),
};

export default StepOne;
